import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { useWindowSize } from '@react-hook/window-size';
import { Amplitude, LogOnMount } from 'react-amplitude-hooks';
import api from 'Api';
import { AuthContext } from 'States/auth/authState';
import i18nextTranslate from 'Lang/i18nextTranslate';
import { i18nextKeys } from 'Lang/i18nextKeys';
import { UiContext } from 'States/ui/uiState';
import usePurchaseMethods from 'Hooks/usePurchaseMethods';
import CustomFooter from 'Components/shared/Custom/CustomFooter';
import ListingCard from './ListingsCard';
import LoadingSpinner from 'Components/shared/LoadingSpinner';
import EmptyState from './EmptyState';

const Listings = () => {
  const {
    user: {
      profile: { sub },
    }
  } = useContext(AuthContext);
  const {
    breakpoints: { sm }
  } = useContext(UiContext);
  const [assetFilter, setAssetFilter] = useState("");
  const [isLoadingUserListings, setLoadingUserListings] = useState(true);
  const GRID_ITEM_WIDTH = 260;
  const GRID_ITEM_HEIGHT = 447;
  const GRID_GAP = 40;
  const [windowWidth, windowHeight] = useWindowSize({ wait: 300 });
  const { ref: pageEndRef, inView } = useInView({ rootMargin: "400px", threshold: 0.1 });
  const fetchLock = useRef(false);

  const pageSize = useMemo(() => {
    const width = windowWidth < 769 ? windowWidth : windowWidth - 300;
    const columns = Math.floor(
      (width + GRID_GAP) / (GRID_ITEM_WIDTH + GRID_GAP)
    );
    const rows = Math.ceil(
      (windowHeight * 1.5) / (GRID_ITEM_HEIGHT + GRID_GAP)
    );

    return Math.max(4, columns * rows);
  }, [windowWidth, windowHeight]);

  useEffect(() => {
    const getListings = async () => {
      try {
        const listingAssetIds = await api.Assets.getListings(sub);
        const assetIdsQuery = [...listingAssetIds].map(id => `'${id}'`).join();
        setAssetFilter(`Id in (${assetIdsQuery})`);
      } catch (error) {
        console.log(error?.response?.data);
      } finally {
        setLoadingUserListings(false);
      }
    }
    getListings();
  }, []);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: isInitialLoadingAssets
  } = usePurchaseMethods.purchaseMethodsPaginatedQuery({
    queryKeys: ["listings"],
    enabled: !!assetFilter,
    errorMessage: i18nextTranslate(i18nextKeys.errorPurchaseAssetLoadingError),
    pageSize,
    filter: assetFilter
  })

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage && !fetchLock.current) {
      fetchLock.current = true;
      fetchNextPage().finally(() => fetchLock.current = false);
    }
  }, [inView, hasNextPage, isFetchingNextPage]);

  return (
    <Amplitude
      eventProperties={{
        scope: 'purchase',
      }}
    >
      <LogOnMount eventType="Shop opened">
        <div
          data-qa="purchase"
          className="h-full flex flex-col justify-between"
        >
          <div className='flex flex-col'>
            <div className="relative flex justify-between md:block mb-20 px-20 md:px-0">
              <div style={{
                fontFamily: "Arsenal, sans-serif",
                fontWeight: 700,
                fontStyle: "normal",
                letterSpacing: "-.04em",
                fontSize: "50px",
                lineHeight: 1,
                width: "fit-content",
              }}>
                {i18nextTranslate(i18nextKeys.customListingsHeading)}
                <div
                  className='h-1 ml-32 mt-10 bg-black'
                  style={{ width: "36%" }}
                ></div>
              </div>
            </div>
            {(isLoadingUserListings || isInitialLoadingAssets) ? (
              <div
                className="flex justify-center px-32 py-24"
                style={{ maxWidth: "940px" }}
              >
                <LoadingSpinner />
              </div>
            ) : data?.pages.length ? (
              <>
                <div
                  className="grid row-gap-32 col-gap-40"
                  style={{
                    gridTemplateColumns: sm
                      ? `repeat(auto-fit, ${GRID_ITEM_WIDTH}px)`
                      : 'repeat(auto-fit, 100%)',
                    gridTemplateRows: 'auto auto auto auto',
                    gap: `${GRID_GAP}px`,
                    placeItems: sm ? 'start' : 'center',
                  }}
                >
                  {data.pages.map((page, pageIndex) => {
                    return page?.assetsConfig.length ? (
                      page.assetsConfig.map((assetConfig, index) => (
                        <ListingCard
                          key={assetConfig.Id + pageIndex + index}
                          dataQa={`purchase-assetCard-${(pageIndex + 1) * (index + 1)}`}
                          assetConfig={assetConfig}
                        />
                      ))
                    ) : null;
                  })}
                </div>
                <div ref={pageEndRef} />
              </>
            ) : <EmptyState style={{ maxWidth: "940px" }} />}
          </div>
          <CustomFooter />
        </div>
      </LogOnMount>
    </Amplitude>
  );
};

export default Listings;
